import React from 'react';
import { Users, Warning } from '@phosphor-icons/react';

interface AtRiskItem {
    cycle_name?: string;
    cycleName?: string;
    cycle_type?: string;
    cycleType?: string;
    overdue_stage?: string;
    overdueStage?: string;
    participants?: number;
    completion_percentage?: number;
    completionPercentage?: number;
}

interface AtRiskCardProps {
    data: AtRiskItem[] | { data: AtRiskItem[]; metadata?: any; meta?: any; schema?: any };
    options?: {
        showDaysOverdue?: boolean;
        showCompletion?: boolean;
        showOwner?: boolean;
        showType?: boolean;
        maxCards?: number;
    };
}

const getCycleTypeConfig = (type: string) => {
    const t = (type || '').toLowerCase();
    if (t === 'standard' || t.includes('performance')) return { color: 'bg-violet-100 text-violet-700', label: 'Performance' };
    if (t.includes('360')) return { color: 'bg-cyan-100 text-cyan-700', label: '360 Review' };
    if (t.includes('check')) return { color: 'bg-teal-100 text-teal-700', label: 'Check-In' };
    if (t.includes('annual') || t.includes('yearly')) return { color: 'bg-purple-100 text-purple-700', label: 'Annual' };
    if (t.includes('quarterly')) return { color: 'bg-blue-100 text-blue-700', label: 'Quarterly' };
    if (t.includes('probation')) return { color: 'bg-orange-100 text-orange-700', label: 'Probation' };
    if (t.includes('pip')) return { color: 'bg-red-100 text-red-700', label: 'PIP' };
    return { color: 'bg-gray-100 text-gray-600', label: type || 'Review' };
};

const getOverdueStageConfig = (stage: string) => {
    const s = (stage || '').toLowerCase();
    if (s.includes('self')) return { color: 'bg-amber-100 text-amber-700 border border-amber-200', label: 'Self Review' };
    if (s.includes('manager')) return { color: 'bg-orange-100 text-orange-700 border border-orange-200', label: 'Manager Review' };
    if (s.includes('hr') || s.includes('admin')) return { color: 'bg-red-100 text-red-700 border border-red-200', label: 'HR Review' };
    return { color: 'bg-gray-100 text-gray-600 border border-gray-200', label: stage || 'Unknown' };
};

const getCompletionColor = (pct: number): string => {
    if (pct < 50) return 'bg-red-500';
    if (pct < 80) return 'bg-amber-500';
    return 'bg-green-500';
};

export const AtRiskCard: React.FC<AtRiskCardProps> = ({ data, options = {} }) => {
    const {
        showCompletion = true,
        showType = true,
        maxCards = 10,
    } = options;

    let items: AtRiskItem[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        items = data.data;
    }

    const displayItems = items.slice(0, maxCards);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-3">
                    <Warning size={28} className="text-green-400" />
                </div>
                <span className="text-sm font-medium">No at-risk cycles</span>
                <span className="text-xs text-gray-400 mt-1">All cycles are on track</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                const name = item.cycleName || item.cycle_name || 'Unnamed Cycle';
                const type = item.cycleType || item.cycle_type || '';
                const overdueStage = item.overdueStage || item.overdue_stage || '';
                const participants = item.participants;
                const completion = item.completionPercentage ?? item.completion_percentage;

                const typeConfig = getCycleTypeConfig(type);
                const stageConfig = getOverdueStageConfig(overdueStage);

                return (
                    <div
                        key={`at-risk-${index}`}
                        className="group bg-white rounded-lg border border-gray-200 border-l-4 border-l-red-400 p-3.5 hover:border-red-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                        {/* Row 1: Name */}
                        <div className="flex items-start justify-between gap-3 mb-2.5">
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-red-600 transition-colors flex-1">
                                {name}
                            </h3>
                        </div>

                        {/* Row 2: Badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                            {showType && type && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${typeConfig.color}`}>
                                    {typeConfig.label}
                                </span>
                            )}
                            {overdueStage && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${stageConfig.color}`}>
                                    <Warning size={10} weight="bold" />
                                    {stageConfig.label}
                                </span>
                            )}
                        </div>

                        {/* Row 3: Completion Progress */}
                        {showCompletion && completion !== undefined && completion !== null && (
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${getCompletionColor(completion)}`}
                                        style={{ width: `${Math.min(completion, 100)}%` }}
                                    />
                                </div>
                                <span className="text-xs font-medium text-gray-600 min-w-[32px] text-right">
                                    {Math.round(completion)}%
                                </span>
                            </div>
                        )}

                        {/* Row 4: Footer */}
                        {participants !== undefined && participants !== null && (
                            <div className="flex items-center text-xs text-gray-500 pt-2.5 border-t border-gray-100">
                                <div className="flex items-center gap-1.5">
                                    <Users size={14} className="text-gray-400" />
                                    <span>{participants} participants</span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
